import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ShoppingBag, ShieldCheck, Clock, Check, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function formatPrice(amount, currency, locale = "en") {
    try {
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: (currency || "EUR").toUpperCase(),
            maximumFractionDigits: 2,
        }).format(amount);
    } catch {
        return `${amount} ${currency}`;
    }
}

export default function MyOrders() {
    const { t, i18n } = useTranslation();
    const { user, loading } = useAuth();
    const [items, setItems] = useState(null);
    const [confirming, setConfirming] = useState(null);

    const reload = async () => {
        try {
            const { data } = await api.get("/marketplace/transactions/mine");
            setItems(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    useEffect(() => {
        if (loading) return;
        if (!user || typeof user !== "object") return;
        reload();
    }, [user, loading]);

    if (loading) return null;
    if (!user || typeof user !== "object") {
        return (
            <main className="min-h-[calc(100vh-73px)] flex flex-col items-center justify-center px-6 text-center">
                <ShoppingBag size={32} strokeWidth={1} className="text-[var(--accent)] mb-4" />
                <p className="font-display text-2xl text-[var(--ink)] mb-3"
                   style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}>
                    {t("marketplace.login_to_buy")}
                </p>
                <Link to="/acceder" className="bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-6 py-2 text-sm uppercase tracking-widest">
                    {t("nav.login")}
                </Link>
            </main>
        );
    }

    const handleConfirm = async (sessionId) => {
        setConfirming(sessionId);
        try {
            await api.post(`/marketplace/transactions/${sessionId}/confirm-receipt`);
            toast.success(t("marketplace.receipt_confirmed"));
            await reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setConfirming(null);
        }
    };

    return (
        <main className="min-h-[calc(100vh-73px)] max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-3">
                {t("marketplace.orders_eyebrow")}
            </p>
            <h1
                className="font-display text-5xl md:text-6xl text-[var(--ink)] tracking-tight mb-4"
                style={{ fontVariationSettings: '"wght" 700, "wdth" 80, "opsz" 144' }}
            >
                {t("marketplace.orders_title")}
            </h1>
            <p className="text-[var(--ink-muted)] text-lg mb-12">{t("marketplace.orders_lead")}</p>

            {items === null ? (
                <p className="font-display text-2xl italic text-[var(--ink)] animate-slow-pulse">…</p>
            ) : items.length === 0 ? (
                <div className="text-center py-20 border border-[var(--border-strong)] bg-[var(--bg-elevated)]">
                    <ShoppingBag size={32} strokeWidth={1} className="mx-auto text-[var(--accent)] mb-4" />
                    <p className="font-display text-2xl text-[var(--ink)] mb-2">{t("marketplace.orders_empty_title")}</p>
                    <p className="text-[var(--ink-muted)] mb-6">{t("marketplace.orders_empty_lead")}</p>
                    <Link to="/marketplace" className="bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-6 py-2.5 text-sm uppercase tracking-widest">
                        {t("marketplace.go_marketplace")}
                    </Link>
                </div>
            ) : (
                <ul className="space-y-4">
                    {items.map((tx, i) => {
                        const released = ["paid_released", "auto_released"].includes(tx.payment_status);
                        const held = tx.payment_status === "paid_held";
                        return (
                            <li
                                key={tx.session_id}
                                data-testid={`order-${i}`}
                                className="flex items-start gap-5 border border-[var(--border)] bg-[var(--bg-elevated)] p-5"
                            >
                                <div className="w-16 sm:w-20 flex-shrink-0 aspect-[2/3] bg-[var(--bg-strong)] border border-[var(--border)]">
                                    {tx.snapshot?.cover_url && (
                                        <img src={tx.snapshot.cover_url} alt={tx.snapshot.title} className="w-full h-full object-cover" />
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--accent)] mb-1">
                                        {t(`marketplace.tx_status_${tx.payment_status}`)}
                                    </p>
                                    <h3 className="font-display text-xl text-[var(--ink)] leading-tight mb-1 truncate"
                                        style={{ fontVariationSettings: '"wght" 600, "wdth" 92, "opsz" 24' }}>
                                        {tx.snapshot?.title}
                                    </h3>
                                    <p className="text-xs text-[var(--ink-muted)] mb-3">{tx.snapshot?.author}</p>
                                    <p className="font-mono-num text-base text-[var(--ink)] mb-3">
                                        {formatPrice(tx.amount, tx.currency, i18n.language)}
                                    </p>
                                    {held && (
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-2">
                                            <button
                                                onClick={() => handleConfirm(tx.session_id)}
                                                disabled={confirming === tx.session_id}
                                                data-testid={`confirm-receipt-${i}`}
                                                className="inline-flex items-center gap-2 bg-[var(--accent)] text-[var(--bg)] rounded-full hover:opacity-90 disabled:opacity-50 transition-opacity px-5 py-2 text-xs uppercase tracking-widest"
                                            >
                                                {confirming === tx.session_id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} strokeWidth={2} />}
                                                {t("marketplace.confirm_receipt_btn")}
                                            </button>
                                            <span className="text-xs text-[var(--ink-dim)] inline-flex items-center gap-1.5">
                                                <Clock size={11} strokeWidth={1.5} />
                                                {tx.days_left != null
                                                    ? t("marketplace.auto_release_in_d", { d: tx.days_left })
                                                    : t("marketplace.escrow_held")}
                                            </span>
                                        </div>
                                    )}
                                    {released && (
                                        <p className="text-xs text-[var(--accent)] inline-flex items-center gap-1.5">
                                            <ShieldCheck size={12} strokeWidth={1.8} /> {t("marketplace.funds_released")}
                                        </p>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </main>
    );
}
